import React, { useCallback, useMemo, useState } from 'react';
import { Col, Label, Row, Table } from 'reactstrap';
import { TransportHistoryReportDto } from '../../requests/useGetTransportHistoryReport';
import { dateSort, dateSortReverse } from '../../Utilities';
import { toAbbreviation } from '../Common/Units';
import { SortableColumnHeader, SortDirection } from '../Common/SortableColumnHeader';
import { isMultiValue, KaSelect, KaSelectOption } from '../Common/KaSelect';

export const reportColumns = [
  'Customer',
  'Destination',
  'Notes',
  'Products',
  'Compartment',
  'Ticket Number',
  'Order Number',
]

interface TransportHistoryReportDisplayProps {
  reportData: TransportHistoryReportDto[];
  columns: string[];
}

const compartmentLabel = (index?: number) =>
  index != null ? `Compartment ${index + 1}` : 'No Compartment';

export const TransportHistoryReportDisplay: React.FC<TransportHistoryReportDisplayProps> = ({ reportData, columns }) => {
  const [generatedAt] = useState(new Date().toLocaleString());
  const [sortDirection, setSortDirection] = useState<SortDirection>(SortDirection.descending);
  const [groupedField, setGroupedField] = useState<string>("Transport");
  const [showTotals, setShowTotals] = useState<boolean>(false);

  const productColumns = useMemo(() => {
    const productMap = new Map<string, string>();

    for (const row of reportData) {
      for (const [product, measurement] of Object.entries(row.productMeasurements)) {
        if (!productMap.has(product) && measurement.unit) {
          productMap.set(product, measurement.unit);
        }
      }
    }

    return Array.from(productMap.entries())
      .map(([name, unit]) => ({ name, unit }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [reportData]);

  const columnDisplayState = reportColumns.map(rc => ({ name: rc, show: columns.includes(rc) }));
  const isColumnSelected = (name: string) => columnDisplayState.find(c => c.name === name)!.show;

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  const allSortedData = useMemo(() =>
    sortDirection === SortDirection.descending
      ? dateSortReverse(reportData, 'loadedAt')
      : dateSort(reportData, 'loadedAt'),
  [reportData, sortDirection]);

  const groupingOptions: KaSelectOption[] = [
    { value: "No Grouping", label: "No Grouping" },
    { value: "Transport", label: "Transport" },
    { value: "Transport - Compartment", label: "Transport - Compartment" }
  ];

  const baseColumnCount =
    1 + // Date/Time (always shown)
    (groupedField === "No Grouping" ? 1 : 0) + // Transport (only in no grouping)
    (isColumnSelected("Order Number") ? 1 : 0) +
    (isColumnSelected("Ticket Number") ? 1 : 0) +
    (isColumnSelected("Compartment") ? 1 : 0) +
    (isColumnSelected("Customer") ? 1 : 0) +
    (isColumnSelected("Destination") ? 1 : 0) +
    (isColumnSelected("Notes") ? 1 : 0) +
    (isColumnSelected("Products") ? productColumns.length : 0);

  const productTotalsForRows = useCallback((rows: TransportHistoryReportDto[]) => {
    const totals: Record<string, number> = {};
    for (const row of rows) {
      for (const product of productColumns) {
        totals[product.name] = (totals[product.name] ?? 0) + (row.productMeasurements[product.name]?.quantity ?? 0);
      }
    }
    return totals;
  }, [productColumns]);

  const grandTotals = useMemo(() => productTotalsForRows(reportData), [reportData, productTotalsForRows]);

  const transactionToDataRow = (row: TransportHistoryReportDto, background: string = 'bg-white') =>
    <tr key={`${row.ticketNumber}-${row.compartmentIndex}`} className={`border-top ${background}`}>
      <td className="py-1 text-nowrap">{formatDate(row.loadedAt)}</td>
      {isColumnSelected("Order Number") && <td className="py-1 text-nowrap">{row.orderNumber || '-'}</td>}
      {isColumnSelected("Ticket Number") && <td className="py-1 text-nowrap">{row.ticketNumber}</td>}
      {isColumnSelected("Compartment") && <td className="py-1 text-nowrap">{compartmentLabel(row.compartmentIndex)}</td>}
      {groupedField === "No Grouping" && <td className="py-1 text-nowrap">{row.transportName}</td>}
      {isColumnSelected("Customer") && <td className="py-1 text-nowrap">{row.customerName || '-'}</td>}
      {isColumnSelected("Destination") && <td className="py-1 text-nowrap">{row.destination || '-'}</td>}
      {isColumnSelected("Notes") && <td className="py-1">{row.notes || '-'}</td>}
      {isColumnSelected("Products") && productColumns.map(product => (
        <td key={product.name} className="text-end py-1 text-nowrap">
          {row.productMeasurements[product.name]?.error
            ? 'N/A'
            : row.productMeasurements[product.name]?.quantity?.toFixed(2) || '0'}
        </td>
      ))}
    </tr>;

  const productTotalsRow = (totals: Record<string, number>, background: string, label: string, labelSize: string) =>
    <tr className={`border-top ${background}`}>
      <td className={`py-1 text-nowrap fw-bold ${labelSize}`} colSpan={baseColumnCount - (showTotals && isColumnSelected("Products") ? productColumns.length : 0)}>
        {label}
      </td>
      {isColumnSelected("Products") && productColumns.map(product => (
        <td key={product.name} className={`text-end py-1 text-nowrap fw-bold ${labelSize}`}>
          {(totals[product.name] ?? 0).toFixed(2)}
        </td>
      ))}
    </tr>;

  const nestedGroups = useMemo(() => {
    if (groupedField !== "Transport") return null;

    const result = new Map<string, Map<string, TransportHistoryReportDto[]>>();

    for (const dto of allSortedData) {
      let compartmentMap = result.get(dto.transportName);
      if (!compartmentMap) {
        compartmentMap = new Map();
        result.set(dto.transportName, compartmentMap);
      }

      const label = compartmentLabel(dto.compartmentIndex);
      let rows = compartmentMap.get(label);
      if (!rows) {
        rows = [];
        compartmentMap.set(label, rows);
      }
      rows.push(dto);
    }

    return result;
  }, [allSortedData, groupedField]);

  const flatGroups = useMemo(() => {
    if (groupedField !== "Transport - Compartment") return null;

    const map = new Map<string, TransportHistoryReportDto[]>();

    for (const dto of allSortedData) {
      const key = `${dto.transportName} - ${compartmentLabel(dto.compartmentIndex)}`;
      let group = map.get(key);
      if (!group) {
        group = [];
        map.set(key, group);
      }
      group.push(dto);
    }

    return map;
  }, [allSortedData, groupedField]);

  const flatGroupTotals = useMemo(() => {
    if (!flatGroups || !showTotals) return null;
    const totals = new Map<string, Record<string, number>>();
    for (const [key, rows] of flatGroups) {
      totals.set(key, productTotalsForRows(rows));
    }
    return totals;
  }, [flatGroups, showTotals, productTotalsForRows]);

  return (
    <div className="container-fluid p-4">
      <Row>
        <Col className="col-auto pe-0">
          <h1 className="ka-blue">Transport History Report</h1>
        </Col>
      </Row>
      <p className="text-muted mb-5">Generated on {generatedAt}</p>
      <Row className="mb-2">
        <Col className="col-2" style={{ zIndex: 1021 }}>
          <Label htmlFor="grouping-selector" className="fw-bold form-label">
            Group Transactions
          </Label>
          <KaSelect
            inputId="grouping-selector"
            placeholder="Group by..."
            options={groupingOptions}
            value={groupingOptions.find(go => go.value === groupedField)}
            onChange={(option) => setGroupedField(isMultiValue(option) ? "Transport" : (option?.value || "Transport"))}
            isClearable={false}
          />
        </Col>
      </Row>
      <Row>
        <Col className="form-check ms-3">
          <input
            type="checkbox"
            id="toggleShowTotals"
            className="form-check-input"
            checked={showTotals}
            onChange={() => setShowTotals(!showTotals)} />
          <label htmlFor="toggleShowTotals" className="form-check-label">
            Show Product Totals
          </label>
        </Col>
      </Row>
      {reportData.length > 0 ? (
        <div className="table">
          <Table borderless>
            <thead className="sticky-top bg-white">
              <tr className="border-bottom border-dark border-1">
                <th>
                  <SortableColumnHeader
                    name="Date/Time"
                    isActive={true}
                    direction={sortDirection}
                    handleSort={() => setSortDirection(d => d === SortDirection.ascending ? SortDirection.descending : SortDirection.ascending)}
                  />
                </th>
                {isColumnSelected("Order Number") && <th>Order #</th>}
                {isColumnSelected("Ticket Number") && <th>Ticket #</th>}
                {isColumnSelected("Compartment") && <th>Compartment</th>}
                {groupedField === "No Grouping" && <th>Transport</th>}
                {isColumnSelected("Customer") && <th>Customer</th>}
                {isColumnSelected("Destination") && <th>Destination</th>}
                {isColumnSelected("Notes") && <th>Notes</th>}
                {isColumnSelected("Products") && productColumns.map(product => (
                  <th key={product.name} className="text-end">
                    {product.name}
                    {product.unit && (
                      <div className="text-muted small">({toAbbreviation(product.unit)})</div>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody key={groupedField}>
              {groupedField === "No Grouping" &&
                allSortedData.map(row => transactionToDataRow(row))}

              {flatGroups !== null && Array.from(flatGroups.entries()).sort(([a], [b]) => a.localeCompare(b)).map(([key, rows], index) =>
                <React.Fragment key={key}>
                  <tr className={`border-top ${index % 2 === 1 ? "bg-row-grouping" : "bg-white"}`}>
                    <td className="py-1 text-nowrap fw-bold fs-4" colSpan={baseColumnCount}>
                      {key}
                    </td>
                  </tr>
                  {rows.map(row => transactionToDataRow(row, index % 2 === 1 ? "bg-row-grouping" : "bg-white"))}
                  {showTotals && flatGroupTotals &&
                    productTotalsRow(flatGroupTotals.get(key) ?? {}, index % 2 === 1 ? "bg-row-grouping" : "bg-white", `${key} Total`, "fs-6")}
                </React.Fragment>
              )}

              {nestedGroups !== null && Array.from(nestedGroups.entries()).sort(([a], [b]) => a.localeCompare(b)).map(([transportName, compartments], tIndex) => {
                const transportRows = Array.from(compartments.values()).flat();
                const transportTotals = productTotalsForRows(transportRows);
                const bg = tIndex % 2 === 1 ? "bg-row-grouping" : "bg-white";
                return <React.Fragment key={transportName}>
                  <tr className={`border-top ${bg}`}>
                    <td className="py-1 text-nowrap fw-bold fs-3" colSpan={baseColumnCount - (showTotals && isColumnSelected("Products") ? productColumns.length : 0)}>
                      {transportName}
                    </td>
                    {showTotals && isColumnSelected("Products") && productColumns.map(product => (
                      <td key={product.name} className="text-end py-1 text-nowrap fw-bold fs-3">
                        {(transportTotals[product.name] ?? 0).toFixed(2)}
                      </td>
                    ))}
                  </tr>
                  {Array.from(compartments.entries()).sort(([a], [b]) => a.localeCompare(b)).map(([label, rows]) => {
                    const compartmentTotals = productTotalsForRows(rows);
                    return <React.Fragment key={`${transportName}-${label}`}>
                      <tr className={`border-top ${bg}`}>
                        <td className="py-1 ps-5 text-nowrap fw-bold fs-6" colSpan={baseColumnCount - (showTotals && isColumnSelected("Products") ? productColumns.length : 0)}>
                          {label}
                        </td>
                        {showTotals && isColumnSelected("Products") && productColumns.map(product => (
                          <td key={product.name} className="text-end py-1 text-nowrap fw-bold fs-6">
                            {(compartmentTotals[product.name] ?? 0).toFixed(2)}
                          </td>
                        ))}
                      </tr>
                      {rows.map(row => transactionToDataRow(row, bg))}
                    </React.Fragment>;
                  })}
                </React.Fragment>;
              })}

              {showTotals && (
                <tr key="totalRow" className={`border-top ${
                  groupedField === "Transport"
                    ? nestedGroups!.size % 2 === 1 ? "bg-row-grouping" : "bg-white"
                    : groupedField === "Transport - Compartment"
                      ? flatGroups!.size % 2 === 1 ? "bg-row-grouping" : "bg-white"
                      : "bg-row-grouping"
                }`}>
                  <td className="py-1 text-nowrap fw-bold fs-3" colSpan={baseColumnCount - (showTotals && isColumnSelected("Products") ? productColumns.length : 0)}>
                    Totals
                  </td>
                  {isColumnSelected("Products") && productColumns.map(product => (
                    <td key={product.name} className="text-end py-1 text-nowrap fw-bold fs-3">
                      {(grandTotals[product.name] ?? 0).toFixed(2)}
                    </td>
                  ))}
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      ) : (
        <div className="text-center py-5 text-muted">
          <i className="bi bi-inbox fs-1 d-block mb-3"></i>
          <h4>No Data Available</h4>
          <p>No transport history found for the selected filters.</p>
        </div>
      )}
    </div>
  );
};
